/* eslint-disable no-unused-vars */
import React, { useState, useEffect } from 'react';
import {
  View,
  Text,
  ScrollView,
  TouchableOpacity,
  StyleSheet,
  SafeAreaView,
  StatusBar,
  Alert,
  Dimensions,
} from 'react-native';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { Ionicons, MaterialCommunityIcons, FontAwesome5 } from '@expo/vector-icons';
import { useNavigation } from '@react-navigation/native';

const { width } = Dimensions.get('window');

const CourseLearningScreen = ({ route }) => {
  const navigation = useNavigation();
  const { course, courseId } = route?.params || {};
  const [userData, setUserData] = useState(null);
  const [currentLesson, setCurrentLesson] = useState(0);
  const [completedLessons, setCompletedLessons] = useState([]);
  const [isPlaying, setIsPlaying] = useState(false);

  // Mixed color scheme for different course categories
  const categoryColors = {
    'Civil Engineering': '#f59e0b', // Orange
    'Mechanical Engineering': '#10b981', // Green
    'Electrical Engineering': '#3b82f6', // Blue
    'Software Engineering': '#8b5cf6', // Purple
    'Chemical Engineering': '#ef4444', // Red
    'Environmental Engineering': '#06b6d4', // Cyan
    'Project Management': '#f97316', // Orange variant
    'Materials Science': '#14b8a6', // Teal
    'Biomedical Engineering': '#84cc16', // Lime
    'Aerospace Engineering': '#6366f1', // Indigo
  };

  const getColorForCategory = (category) => {
    return categoryColors[category] || '#f59e0b';
  };

  useEffect(() => {
    loadUserData();
    loadProgress();
  }, []);

  const loadUserData = async () => {
    try {
      const storedUser = await AsyncStorage.getItem('user');
      const storedUserData = await AsyncStorage.getItem('userData');
      
      if (storedUser) {
        const parsedUser = JSON.parse(storedUser);
        setUserData(parsedUser);
      } else if (storedUserData) {
        const parsedUserData = JSON.parse(storedUserData);
        setUserData(parsedUserData);
      }
    } catch (error) {
      console.error('Error loading user data:', error);
    }
  };

  const loadProgress = async () => {
    try {
      const courseKey = `course_progress_${course?.id || courseId || 'default'}`;
      const storedProgress = await AsyncStorage.getItem(courseKey);
      if (storedProgress) {
        const progress = JSON.parse(storedProgress);
        setCurrentLesson(progress.currentLesson || 0);
        setCompletedLessons(progress.completedLessons || []);
      }
    } catch (error) {
      console.error('Error loading progress:', error);
    }
  };

  const saveProgress = async () => {
    try {
      const courseKey = `course_progress_${course?.id || courseId || 'default'}`;
      const progress = {
        currentLesson,
        completedLessons,
        lastAccessed: new Date().toISOString(),
      };
      await AsyncStorage.setItem(courseKey, JSON.stringify(progress));
    } catch (error) {
      console.error('Error saving progress:', error);
    }
  };

  // Enhanced sample course data with mixed colors and proper fallbacks
  const courseData = {
    id: course?.id || courseId || '1',
    title: course?.title || 'Structural Analysis Fundamentals',
    instructor: course?.instructor || 'Dr. Sarah Johnson',
    category: course?.category || 'Civil Engineering',
    color: getColorForCategory(course?.category || 'Civil Engineering'),
    icon: course?.icon || 'bridge',
    progress: course?.progress || 0,
    modules: course?.modules || [
      {
        id: '1',
        title: 'Introduction to Structural Analysis',
        duration: '2.5 hours',
        color: '#f59e0b', // Orange
        lessons: [
          {
            id: '1-1',
            title: 'What is Structural Analysis?',
            duration: '15 min',
            type: 'video',
            completed: false,
            description: 'Introduction to the fundamentals of structural analysis and its importance in civil engineering.'
          },
          {
            id: '1-2',
            title: 'Types of Structures',
            duration: '20 min',
            type: 'video',
            completed: false,
            description: 'Overview of different structural systems: beams, columns, trusses, and frames.'
          },
          {
            id: '1-3',
            title: 'Load Types and Classifications',
            duration: '18 min',
            type: 'video',
            completed: false,
            description: 'Understanding dead loads, live loads, wind loads, and seismic forces.'
          },
          {
            id: '1-4',
            title: 'Quiz: Structural Basics',
            duration: '10 min',
            type: 'quiz',
            completed: false,
            description: 'Test your understanding of basic structural analysis concepts.'
          }
        ]
      },
      {
        id: '2',
        title: 'Mathematical Foundations',
        duration: '3.5 hours',
        color: '#10b981', // Green
        lessons: [
          {
            id: '2-1',
            title: 'Statics and Equilibrium',
            duration: '25 min',
            type: 'video',
            completed: false,
            description: 'Fundamental principles of statics and force equilibrium in structures.'
          },
          {
            id: '2-2',
            title: 'Free Body Diagrams',
            duration: '30 min',
            type: 'video',
            completed: false,
            description: 'Creating and analyzing free body diagrams for structural elements.'
          },
          {
            id: '2-3',
            title: 'Moment and Shear Calculations',
            duration: '35 min',
            type: 'video',
            completed: false,
            description: 'Calculating bending moments and shear forces in beams and frames.'
          },
          {
            id: '2-4',
            title: 'Practice Problems',
            duration: '20 min',
            type: 'exercise',
            completed: false,
            description: 'Hands-on practice with structural analysis calculations.'
          }
        ]
      },
      {
        id: '3',
        title: 'Advanced Analysis Methods',
        duration: '4 hours',
        color: '#3b82f6', // Blue
        lessons: [
          {
            id: '3-1',
            title: 'Matrix Methods',
            duration: '30 min',
            type: 'video',
            completed: false,
            description: 'Introduction to matrix methods for structural analysis.'
          },
          {
            id: '3-2',
            title: 'Finite Element Basics',
            duration: '35 min',
            type: 'video',
            completed: false,
            description: 'Fundamentals of finite element analysis for structures.'
          },
          {
            id: '3-3',
            title: 'Computer-Aided Analysis',
            duration: '40 min',
            type: 'video',
            completed: false,
            description: 'Using software tools for structural analysis and design.'
          },
          {
            id: '3-4',
            title: 'Final Project',
            duration: '45 min',
            type: 'project',
            completed: false,
            description: 'Complete structural analysis project using learned concepts.'
          }
        ]
      }
    ]
  };

  // Coerce non-array values to [] — backend has historically returned objects
  // or null here, and `|| []` doesn't catch the object case.
  const asArray = (v) => (Array.isArray(v) ? v : []);

  const allLessons = asArray(courseData.modules).flatMap(module =>
    asArray(module.lessons).map(lesson => ({
      ...lesson,
      moduleTitle: module.title,
      moduleColor: module.color || courseData.color
    }))
  );

  const currentLessonData = allLessons[currentLesson] || {};

  const handlePlayPause = () => {
    setIsPlaying(!isPlaying);
    if (currentLessonData?.type === 'video') {
      Alert.alert(
        isPlaying ? 'Video Paused' : 'Video Playing',
        `${isPlaying ? 'Paused' : 'Playing'}: ${currentLessonData.title || 'Current lesson'}`
      );
    } else if (currentLessonData?.type === 'quiz') {
      Alert.alert('Interactive Quiz', 'Starting quiz session...');
    } else if (currentLessonData?.type === 'exercise') {
      Alert.alert('Practice Exercise', 'Opening practice problems...');
    } else if (currentLessonData?.type === 'project') {
      Alert.alert('Project Assignment', 'Loading project requirements...');
    }
  };

  const handleNextLesson = () => {
    if (currentLesson < allLessons.length - 1) {
      // Mark current lesson as completed
      if (currentLessonData?.id && !completedLessons.includes(currentLessonData.id)) {
        const newCompleted = [...completedLessons, currentLessonData.id];
        setCompletedLessons(newCompleted);
      }
      setCurrentLesson(currentLesson + 1);
      setIsPlaying(false);
      saveProgress();
    } else {
      Alert.alert(
        'Course Complete!', 
        'Congratulations! You have completed all lessons in this course.',
        [
          { text: 'View Certificate', onPress: () => navigation.navigate('LearningCertificateScreen') },
          { text: 'Continue Learning', onPress: () => navigation.navigate('LearningCoursesScreen') }
        ]
      );
    }
  };

  const handlePreviousLesson = () => {
    if (currentLesson > 0) {
      setCurrentLesson(currentLesson - 1);
      setIsPlaying(false);
      saveProgress();
    }
  };

  const handleLessonSelect = (index) => {
    if (index >= 0 && index < allLessons.length) {
      setCurrentLesson(index);
      setIsPlaying(false);
      saveProgress();
    }
  };

  const handleMarkComplete = () => {
    if (currentLessonData?.id && !completedLessons.includes(currentLessonData.id)) {
      const newCompleted = [...completedLessons, currentLessonData.id];
      setCompletedLessons(newCompleted);
      saveProgress();
      Alert.alert('Lesson Completed!', `"${currentLessonData.title}" marked as complete.`);
    } else {
      Alert.alert('Already Completed', 'This lesson is already marked as complete.');
    }
  };

  const getProgressPercentage = () => {
    if (allLessons.length === 0) return 0;
    return Math.round((completedLessons.length / allLessons.length) * 100);
  };

  const getLessonIcon = (type) => {
    switch (type) {
      case 'video':
        return 'play-circle';
      case 'quiz':
        return 'help-circle';
      case 'exercise':
        return 'pencil';
      case 'project':
        return 'briefcase';
      default:
        return 'book-open';
    }
  };

  const getLessonTypeColor = (type) => {
    switch (type) {
      case 'video':
        return '#f59e0b'; // Orange
      case 'quiz':
        return '#fdb913'; // Golden
      case 'exercise':
        return '#10b981'; // Green
      case 'project':
        return '#8b5cf6'; // Purple
      default:
        return '#3b82f6'; // Blue
    }
  };

  const getUserName = () => {
    if (!userData) return 'Student';
    return userData.full_name || userData.fullName || userData.name || 
           `${userData.first_name || userData.firstName || ''} ${userData.last_name || userData.lastName || ''}`.trim() || 
           'Student';
  };

  return (
    <SafeAreaView style={styles.safeArea}>
      <StatusBar barStyle="dark-content" backgroundColor="#f9fafb" />
      
      {/* Header */}
      <View style={styles.header}>
        <TouchableOpacity
          onPress={() => navigation.goBack()}
          style={styles.backButton}
        >
          <Ionicons name="arrow-back" size={24} color={courseData.color} />
        </TouchableOpacity>
        <View style={styles.headerInfo}>
          <Text style={[styles.headerTitle, { color: courseData.color }]} numberOfLines={1}>
            {courseData.title}
          </Text>
          <Text style={styles.headerSubtitle}>
            Lesson {currentLesson + 1} of {allLessons.length}
          </Text>
        </View>
        <TouchableOpacity style={styles.menuButton}>
          <MaterialCommunityIcons name="dots-vertical" size={24} color="#6b7280" />
        </TouchableOpacity>
      </View>

      {/* Progress Bar */}
      <View style={styles.progressContainer}>
        <View style={styles.progressInfo}>
          <Text style={styles.progressLabel}>Course Progress</Text>
          <Text style={[styles.progressPercentage, { color: courseData.color }]}>
            {getProgressPercentage()}%
          </Text>
        </View>
        <View style={styles.progressBar}>
          <View 
            style={[
              styles.progressFill, 
              { 
                width: `${getProgressPercentage()}%`,
                backgroundColor: courseData.color
              }
            ]} 
          />
        </View>
        <Text style={styles.progressText}>
          {completedLessons.length} of {allLessons.length} lessons completed
        </Text>
      </View>

      <ScrollView style={styles.container} showsVerticalScrollIndicator={false}>
        {/* Welcome Message */}
        <View style={styles.welcomeCard}>
          <Text style={styles.welcomeText}>Welcome back, {getUserName()}!</Text>
          <Text style={styles.welcomeSubtext}>Continue your learning journey</Text>
        </View>

        {/* Current Lesson Card */}
        <View style={[styles.currentLessonCard, { borderTopColor: courseData.color }]}>
          <View style={styles.lessonHeader}>
            <View style={[
              styles.lessonIconContainer, 
              { backgroundColor: getLessonTypeColor(currentLessonData?.type) }
            ]}>
              <MaterialCommunityIcons 
                name={getLessonIcon(currentLessonData?.type)} 
                size={32} 
                color="#ffffff" 
              />
            </View>
            <View style={styles.lessonInfo}>
              <Text style={styles.moduleTitle}>{currentLessonData?.moduleTitle || 'Module'}</Text>
              <Text style={[styles.lessonTitle, { color: courseData.color }]}>
                {currentLessonData?.title || 'Lesson Title'}
              </Text>
              <Text style={styles.lessonDuration}>{currentLessonData?.duration || 'Duration'}</Text>
            </View>
            <View style={styles.lessonBadge}>
              <Text style={[styles.lessonBadgeText, { color: getLessonTypeColor(currentLessonData?.type) }]}>
                {currentLessonData?.type?.toUpperCase() || 'LESSON'}
              </Text>
            </View>
          </View>

          {/* Lesson Description */}
          <Text style={styles.lessonDescription}>
            {currentLessonData?.description || 'Lesson description will appear here.'}
          </Text>

          {/* Video Player Placeholder */}
          <View style={[styles.videoPlayer, { borderColor: courseData.color }]}>
            <TouchableOpacity 
              style={[styles.playButton, { backgroundColor: courseData.color }]} 
              onPress={handlePlayPause}
            >
              <MaterialCommunityIcons 
                name={isPlaying ? "pause" : "play"} 
                size={48} 
                color="#ffffff" 
              />
            </TouchableOpacity>
            <Text style={styles.videoTitle}>
              {currentLessonData?.type === 'video' ? 'Video Lesson' : 
               currentLessonData?.type === 'quiz' ? 'Interactive Quiz' :
               currentLessonData?.type === 'exercise' ? 'Practice Exercise' :
               currentLessonData?.type === 'project' ? 'Project Assignment' :
               'Learning Content'}
            </Text>
            <Text style={styles.videoSubtitle}>
              {isPlaying ? 'Playing...' : 'Tap to start'}
            </Text>
          </View>

          {/* Lesson Controls */}
          <View style={styles.lessonControls}>
            <TouchableOpacity 
              style={[
                styles.controlButton, 
                currentLesson === 0 && styles.disabledButton,
                { borderColor: courseData.color }
              ]}
              onPress={handlePreviousLesson}
              disabled={currentLesson === 0}
            >
              <MaterialCommunityIcons name="skip-previous" size={20} color={currentLesson === 0 ? "#9ca3af" : courseData.color} />
              <Text style={[styles.controlButtonText, { color: currentLesson === 0 ? "#9ca3af" : courseData.color }]}>
                Previous
              </Text>
            </TouchableOpacity>

            <TouchableOpacity 
              style={[
                styles.completeButton,
                { backgroundColor: completedLessons.includes(currentLessonData?.id) ? '#10b981' : courseData.color }
              ]}
              onPress={handleMarkComplete}
            >
              <MaterialCommunityIcons 
                name={completedLessons.includes(currentLessonData?.id) ? "check-circle" : "check"} 
                size={20} 
                color="#ffffff" 
              />
              <Text style={styles.completeButtonText}>
                {completedLessons.includes(currentLessonData?.id) ? 'Completed' : 'Mark Complete'}
              </Text>
            </TouchableOpacity>

            <TouchableOpacity 
              style={[
                styles.controlButton,
                currentLesson === allLessons.length - 1 && styles.disabledButton,
                { borderColor: courseData.color }
              ]}
              onPress={handleNextLesson}
              disabled={currentLesson === allLessons.length - 1}
            >
              <Text style={[
                styles.controlButtonText, 
                { color: currentLesson === allLessons.length - 1 ? "#9ca3af" : courseData.color }
              ]}>
                Next
              </Text>
              <MaterialCommunityIcons 
                name="skip-next" 
                size={20} 
                color={currentLesson === allLessons.length - 1 ? "#9ca3af" : courseData.color} 
              />
            </TouchableOpacity>
          </View>
        </View>

        {/* Course Content */}
        <View style={styles.contentCard}>
          <Text style={[styles.sectionTitle, { color: courseData.color }]}>Course Content</Text>
          
          {asArray(courseData.modules).map((module, moduleIndex) => (
            <View key={module.id || moduleIndex} style={styles.moduleSection}>
              <View style={styles.moduleHeader}>
                <View style={[styles.moduleIcon, { backgroundColor: module.color || courseData.color }]}>
                  <MaterialCommunityIcons name="folder-open" size={20} color="#ffffff" />
                </View>
                <View style={styles.moduleInfo}>
                  <Text style={[styles.moduleHeaderText, { color: courseData.color }]}>
                    {module.title || `Module ${moduleIndex + 1}`}
                  </Text>
                  <Text style={styles.moduleDuration}>
                    {module.duration || 'Duration TBD'} • {asArray(module.lessons).length} lessons
                  </Text>
                </View>
              </View>

              {asArray(module.lessons).map((lesson, lessonIndex) => {
                const globalIndex = asArray(courseData.modules)
                  .slice(0, moduleIndex)
                  .reduce((acc, mod) => acc + asArray(mod.lessons).length, 0) + lessonIndex;
                
                const isCompleted = completedLessons.includes(lesson.id);
                const isCurrent = globalIndex === currentLesson;
                
                return (
                  <TouchableOpacity
                    key={lesson.id || `${moduleIndex}-${lessonIndex}`}
                    style={[
                      styles.lessonItem,
                      isCurrent && [styles.currentLessonItem, { borderLeftColor: courseData.color }],
                      isCompleted && styles.completedLessonItem
                    ]}
                    onPress={() => handleLessonSelect(globalIndex)}
                  >
                    <View style={styles.lessonItemLeft}>
                      <View style={[
                        styles.lessonItemIcon,
                        { backgroundColor: getLessonTypeColor(lesson.type) }
                      ]}>
                        <MaterialCommunityIcons 
                          name={getLessonIcon(lesson.type)} 
                          size={16} 
                          color="#ffffff" 
                        />
                      </View>
                      <View style={styles.lessonItemInfo}>
                        <Text style={[
                          styles.lessonItemTitle,
                          isCurrent && [styles.currentLessonText, { color: courseData.color }],
                          isCompleted && styles.completedLessonText
                        ]}>
                          {lesson.title || `Lesson ${lessonIndex + 1}`}
                        </Text>
                        <Text style={styles.lessonItemDuration}>
                          {lesson.duration || 'Duration TBD'}
                        </Text>
                      </View>
                    </View>
                    
                    <View style={styles.lessonItemRight}>
                      {isCompleted && (
                        <MaterialCommunityIcons name="check-circle" size={20} color="#10b981" />
                      )}
                      {isCurrent && !isCompleted && (
                        <MaterialCommunityIcons name="play-circle" size={20} color={courseData.color} />
                      )}
                      {!isCurrent && !isCompleted && (
                        <MaterialCommunityIcons name="circle-outline" size={20} color="#d1d5db" />
                      )}
                    </View>
                  </TouchableOpacity>
                );
              })}
            </View>
          ))}
        </View>

        {/* Course Statistics */}
        <View style={styles.statsCard}>
          <Text style={[styles.sectionTitle, { color: courseData.color }]}>Your Progress</Text>
          <View style={styles.statsGrid}>
            <View style={styles.statItem}>
              <View style={[styles.statIcon, { backgroundColor: '#f59e0b' }]}>
                <MaterialCommunityIcons name="book-open-variant" size={24} color="#ffffff" />
              </View>
              <Text style={styles.statValue}>{allLessons.length}</Text>
              <Text style={styles.statLabel}>Total Lessons</Text>
            </View>
            <View style={styles.statItem}>
              <View style={[styles.statIcon, { backgroundColor: '#10b981' }]}>
                <MaterialCommunityIcons name="check-circle" size={24} color="#ffffff" />
              </View>
              <Text style={styles.statValue}>{completedLessons.length}</Text>
              <Text style={styles.statLabel}>Completed</Text>
            </View>
            <View style={styles.statItem}>
              <View style={[styles.statIcon, { backgroundColor: '#3b82f6' }]}>
                <MaterialCommunityIcons name="clock" size={24} color="#ffffff" />
              </View>
              <Text style={styles.statValue}>{allLessons.length - completedLessons.length}</Text>
              <Text style={styles.statLabel}>Remaining</Text>
            </View>
            <View style={styles.statItem}>
              <View style={[styles.statIcon, { backgroundColor: courseData.color }]}>
                <MaterialCommunityIcons name="trophy" size={24} color="#ffffff" />
              </View>
              <Text style={styles.statValue}>{getProgressPercentage()}%</Text>
              <Text style={styles.statLabel}>Progress</Text>
            </View>
          </View>
        </View>
      </ScrollView>
    </SafeAreaView>
  );
};

const styles = StyleSheet.create({
  safeArea: {
    flex: 1,
    backgroundColor: '#f9fafb',
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 15,
    paddingVertical: 12,
    backgroundColor: '#fff',
    borderBottomWidth: 1,
    borderBottomColor: '#e5e7eb',
  },
  backButton: {
    padding: 5,
  },
  headerInfo: {
    flex: 1,
    alignItems: 'center',
    marginHorizontal: 15,
  },
  headerTitle: {
    fontSize: 18,
    fontWeight: 'bold',
  },
  headerSubtitle: {
    fontSize: 12,
    color: '#6b7280',
    marginTop: 2,
  },
  menuButton: {
    padding: 5,
  },
  progressContainer: {
    backgroundColor: '#fff',
    paddingHorizontal: 15,
    paddingVertical: 12,
    borderBottomWidth: 1,
    borderBottomColor: '#e5e7eb',
  },
  progressInfo: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 8,
  },
  progressLabel: {
    fontSize: 14,
    fontWeight: '500',
    color: '#374151',
  },
  progressPercentage: {
    fontSize: 16,
    fontWeight: 'bold',
  },
  progressBar: {
    height: 8,
    backgroundColor: '#e5e7eb',
    borderRadius: 4,
    marginBottom: 8,
  },
  progressFill: {
    height: '100%',
    borderRadius: 4,
  },
  progressText: {
    fontSize: 12,
    color: '#6b7280',
    textAlign: 'center',
  },
  container: {
    flex: 1,
    padding: 15,
  },
  welcomeCard: {
    backgroundColor: '#fff',
    borderRadius: 12,
    padding: 16,
    marginBottom: 15,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 3,
    elevation: 3,
  },
  welcomeText: {
    fontSize: 18,
    fontWeight: 'bold',
    color: '#111827',
    marginBottom: 4,
  },
  welcomeSubtext: {
    fontSize: 14,
    color: '#6b7280',
  },
  currentLessonCard: {
    backgroundColor: '#fff',
    borderRadius: 12,
    padding: 20,
    marginBottom: 15,
    borderTopWidth: 4,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 3,
    elevation: 3,
  },
  lessonHeader: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    marginBottom: 16,
  },
  lessonIconContainer: {
    width: 60,
    height: 60,
    borderRadius: 12,
    justifyContent: 'center',
    alignItems: 'center',
    marginRight: 16,
  },
  lessonInfo: {
    flex: 1,
  },
  moduleTitle: {
    fontSize: 12,
    color: '#6b7280',
    marginBottom: 4,
    textTransform: 'uppercase',
    fontWeight: '500',
  },
  lessonTitle: {
    fontSize: 18,
    fontWeight: 'bold',
    marginBottom: 4,
  },
  lessonDuration: {
    fontSize: 14,
    color: '#6b7280',
  },
  lessonBadge: {
    backgroundColor: '#f3f4f6',
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: 12,
  },
  lessonBadgeText: {
    fontSize: 10,
    fontWeight: '600',
  },
  lessonDescription: {
    fontSize: 14,
    color: '#374151',
    lineHeight: 20,
    marginBottom: 16,
  },
  videoPlayer: {
    height: 200,
    backgroundColor: '#1f2937',
    borderRadius: 12,
    justifyContent: 'center',
    alignItems: 'center',
    marginBottom: 16,
    borderWidth: 2,
  },
  playButton: {
    width: 80,
    height: 80,
    borderRadius: 40,
    justifyContent: 'center',
    alignItems: 'center',
    marginBottom: 12,
  },
  videoTitle: {
    color: '#ffffff',
    fontSize: 16,
    fontWeight: '600',
    marginBottom: 4,
  },
  videoSubtitle: {
    color: '#d1d5db',
    fontSize: 14,
  },
  lessonControls: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: 12,
  },
  controlButton: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 16,
    paddingVertical: 12,
    borderRadius: 8,
    flex: 0.3,
    justifyContent: 'center',
    borderWidth: 2,
    backgroundColor: '#fff',
  },
  completeButton: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 16,
    paddingVertical: 12,
    borderRadius: 8,
    flex: 0.4,
    justifyContent: 'center',
  },
  disabledButton: {
    borderColor: '#d1d5db',
    backgroundColor: '#f9fafb',
  },
  controlButtonText: {
    fontSize: 14,
    fontWeight: '500',
    marginHorizontal: 4,
  },
  completeButtonText: {
    color: '#ffffff',
    fontSize: 14,
    fontWeight: '500',
    marginHorizontal: 4,
  },
  contentCard: {
    backgroundColor: '#fff',
    borderRadius: 12,
    padding: 20,
    marginBottom: 15,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 3,
    elevation: 3,
  },
  sectionTitle: {
    fontSize: 18,
    fontWeight: 'bold',
    marginBottom: 16,
  },
  moduleSection: {
    marginBottom: 20,
  },
  moduleHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 12,
  },
  moduleIcon: {
    width: 40,
    height: 40,
    borderRadius: 8,
    justifyContent: 'center',
    alignItems: 'center',
    marginRight: 12,
  },
  moduleInfo: {
    flex: 1,
  },
  moduleHeaderText: {
    fontSize: 16,
    fontWeight: 'bold',
    marginBottom: 2,
  },
  moduleDuration: {
    fontSize: 12,
    color: '#6b7280',
  },
  lessonItem: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingVertical: 12,
    paddingHorizontal: 12,
    borderRadius: 8,
    marginBottom: 8,
    backgroundColor: '#f9fafb',
  },
  currentLessonItem: {
    backgroundColor: '#eff6ff',
    borderLeftWidth: 4,
  },
  completedLessonItem: {
    backgroundColor: '#f0fdf4',
  },
  lessonItemLeft: {
    flexDirection: 'row',
    alignItems: 'center',
    flex: 1,
  },
  lessonItemIcon: {
    width: 32,
    height: 32,
    borderRadius: 6,
    justifyContent: 'center',
    alignItems: 'center',
    marginRight: 12,
  },
  lessonItemInfo: {
    flex: 1,
  },
  lessonItemTitle: {
    fontSize: 14,
    fontWeight: '500',
    color: '#111827',
    marginBottom: 2,
  },
  currentLessonText: {
    fontWeight: 'bold',
  },
  completedLessonText: {
    color: '#10b981',
  },
  lessonItemDuration: {
    fontSize: 12,
    color: '#6b7280',
  },
  lessonItemRight: {
    marginLeft: 12,
  },
  statsCard: {
    backgroundColor: '#fff',
    borderRadius: 12,
    padding: 20,
    marginBottom: 20,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 3,
    elevation: 3,
  },
  statsGrid: {
    flexDirection: 'row',
    justifyContent: 'space-between',
  },
  statItem: {
    alignItems: 'center',
    flex: 1,
  },
  statIcon: {
    width: 48,
    height: 48,
    borderRadius: 24,
    justifyContent: 'center',
    alignItems: 'center',
    marginBottom: 8,
  },
  statValue: {
    fontSize: 18,
    fontWeight: 'bold',
    color: '#111827',
    marginBottom: 4,
  },
  statLabel: {
    fontSize: 12,
    color: '#6b7280',
    textAlign: 'center',
  },
});

export default CourseLearningScreen;
